import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getSupabase, isSupabaseConfigured } from "./supabase";

/**
 * MER-245: resolve the URL slug (`/:slug/*`) to its tenant_id and bind
 * that id onto every customer-data query (see lib/tenantScope). RLS is
 * defense in depth only: a non-impersonating staff session matches all
 * rows, so without an application-layer tenant filter the dashboard
 * blends tenants and the largest one's data dominates every aggregate
 * (the /bestdisposal-shows-DWS-numbers bug).
 *
 * The `tenants` table (slug -> id) is the binding source, not customer
 * data, so the lookup itself is exempt from the unscoped-query lint.
 */

const cache = new Map<string, Promise<string | null>>();

/**
 * slug -> tenant id, once a lookup has answered. Lets a component that mounts
 * after the first lookup (every report tab the reader opens) start with the
 * id instead of a render with none (AJ 2026-10-01, speed).
 */
const resolved = new Map<string, string>();

/**
 * Resolve a customer slug to its tenant UUID. Cached per slug with a
 * single in-flight promise. Nulls and rejections are evicted so a later
 * call can retry (a missing slug today may exist after onboarding).
 */
export async function resolveTenantId(slug: string | undefined): Promise<string | null> {
  if (!slug) return null;
  const existing = cache.get(slug);
  if (existing) return existing;

  const pending = (async (): Promise<string | null> => {
    if (!isSupabaseConfigured()) return null;
    const { data, error } = await getSupabase()
      .from("tenants")
      .select("id")
      .eq("slug", slug)
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message ?? "tenant lookup failed");
    return (data?.id as string | undefined) ?? null;
  })();

  cache.set(slug, pending);
  pending.then(
    (id) => {
      if (id === null) cache.delete(slug);
      else resolved.set(slug, id);
    },
    () => cache.delete(slug),
  );
  return pending;
}

/**
 * The resolved tenant UUID for the current `/:slug/*` route, or null
 * while resolving / when the slug has no tenant. Fail-closed: query
 * effects gate on a non-null value before scoping, so a null id never
 * fires an unscoped customer-data query.
 *
 * Reads the slug straight from the route params, so it works in any
 * component or hook rendered under the customer route without a
 * provider to thread through.
 */
export function useTenantId(): string | null {
  const { slug } = useParams<{ slug: string }>();
  const [tenantId, setTenantId] = useState<string | null>(() =>
    slug ? (resolved.get(slug) ?? null) : null,
  );

  useEffect(() => {
    let cancelled = false;
    // The id this slug already resolved to, or null while its lookup is out:
    // never the previous slug's id.
    const known = slug ? (resolved.get(slug) ?? null) : null;
    setTenantId(known);
    if (!slug || known) return;
    resolveTenantId(slug)
      .then((id) => {
        if (!cancelled) setTenantId(id);
      })
      .catch(() => {
        if (!cancelled) setTenantId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return tenantId;
}

/**
 * 2026-10-05: the slug a resolved tenant id belongs to, or null when this
 * page has not resolved it. public.uploads is keyed by the slug (its text
 * customer_id), not the tenant uuid; the freshness read needs it.
 */
export function slugForTenantId(tenantId: string): string | null {
  for (const [slug, id] of resolved) if (id === tenantId) return slug;
  return null;
}

/** Test-only: drop the slug -> tenant_id cache. */
export function _resetTenantCacheForTests(): void {
  cache.clear();
  resolved.clear();
}
